// Extract a poster frame from a video File entirely in the browser, without
// reading the whole file into memory: an object URL lets the <video> element
// stream just the metadata + the one frame we seek to, so this stays cheap even
// for multi-hundred-MB uploads. Returns a small JPEG data URL, or null if the
// browser can't decode the video (unsupported codec, DRM, error, timeout).
export function extractVideoPoster(file: File, maxDim = 320): Promise<string | null> {
  return new Promise((resolve) => {
    if (typeof document === 'undefined' || !file.type.startsWith('video/')) { resolve(null); return; }

    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    (video as HTMLVideoElement & { playsInline?: boolean }).playsInline = true;
    video.crossOrigin = 'anonymous';

    let settled = false;
    const cleanup = () => {
      URL.revokeObjectURL(url);
      video.removeAttribute('src');
      try { video.load(); } catch { /* ignore */ }
    };
    const finish = (result: string | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      cleanup();
      resolve(result);
    };
    const timer = setTimeout(() => finish(null), 10_000);

    video.onloadedmetadata = () => {
      // Seek a little in so we skip black/blank opening frames — 1s, or 10% for
      // very short clips. Guard against NaN/Infinity durations (streams).
      const target = Math.min(1, (Number.isFinite(video.duration) ? video.duration : 2) * 0.1);
      try { video.currentTime = target > 0 ? target : 0; } catch { finish(null); }
    };

    video.onseeked = () => {
      try {
        const w = video.videoWidth;
        const h = video.videoHeight;
        if (!w || !h) { finish(null); return; }
        const scale = Math.min(1, maxDim / Math.max(w, h));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(w * scale));
        canvas.height = Math.max(1, Math.round(h * scale));
        const ctx = canvas.getContext('2d');
        if (!ctx) { finish(null); return; }
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        finish(canvas.toDataURL('image/jpeg', 0.7));
      } catch {
        finish(null); // tainted canvas / decode failure
      }
    };

    video.onerror = () => finish(null);
    video.src = url;
  });
}
